import * as React from "react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  getSavedCases,
  saveCases,
  SavedCase,
} from "../../../saveCase/savedCases";
import { KahunPrimaryTypography } from "fe-web-components/styles/typography";
import { Button, IconButton, styled } from "@mui/material";
import moment from "moment";
import { __RouterContext, useHistory } from "react-router";
import { AgentRoute } from "../util/agentRoutes";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import LaunchIcon from "@mui/icons-material/Launch";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  MobileView,
  SingleViewPerLayoutBreakPoint,
} from "../../../util/aKaunGeneralUtil";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { scrollableStyle } from "fe-common/ui/commonStyles";
import { BackButton } from "../common/backButton";
import { AgentTooltip } from "fe-common/ui/AgentTooltip";
import { getCurrentUserEmail } from "user-management/src/cognito/kahunUserAuth";

//region [[ Styles ]]
const MyCasesWrapper = styled("div")(({ theme }) => ({
  backgroundColor: "transparent",
  position: "relative",
  height: "100%",
  [theme.breakpoints.down(MobileView)]: {
    ...scrollableStyle,
  },
}));

const MyCasesTitle = styled(KahunPrimaryTypography)(({ theme }) => ({
  color: KahunColorMap.Default,
  fontSize: "2em",
  marginBottom: "30px",
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
  [theme.breakpoints.down(MobileView)]: {
    fontSize: "1.5em",
    marginBottom: "15px",
  },
  [theme.breakpoints.up(SingleViewPerLayoutBreakPoint)]: {
    marginLeft: "-60px",
  },
}));

const CaseItem = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  flexWrap: "wrap",
  [theme.breakpoints.down(MobileView)]: {
    margin: theme.spacing(0.5),
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderBottom: `1px solid ${KahunColorMap.Default}`,
    "&:last-of-type": {
      border: "none",
    },
  },
}));

const CaseName = styled(KahunPrimaryTypography)(({ theme }) => ({
  flex: "3 55%",
  color: KahunColorMap.Default,
  [theme.breakpoints.down(MobileView)]: {
    flex: "3 100%",
  },
}));

const CaseCreationDate = styled(KahunPrimaryTypography)(({ theme }) => ({
  flex: "1 25%",
  textAlign: "center",
  color: KahunColorMap.Default,
  [theme.breakpoints.down(MobileView)]: {
    flex: "1 50%",
  },
}));

const Launch = styled(LaunchIcon)(({ theme }) => ({}));

const OpenCaseButton = styled(Button)({
  padding: "8px 10px",
  margin: "5px",
  border: `none`,
  color: `${KahunColorMap.Primary}`,
  "& svg": {
    fill: `${KahunColorMap.Primary}`,
  },
  textTransform: "none",
  borderRadius: "10px",
  flex: 1,
});

const Delete = styled(IconButton)({
  flex: 1,
  "& svg": { fill: `${KahunColorMap.Primary}` },
});

const Back = styled(BackButton)({});
//endregion [[ Styles ]]

//region [[ Functions ]]

function useSafeRouterLocation() {
  return useContext(__RouterContext)?.location;
}
// If 30 minutes+ had passed from when the case was saved, display the full date
const MAX_NUMBER_IN_MINUTES_TO_SHOW_MOMENT_TEXT = 30;
//endregion [[ Functions ]]

export const CasesListsRoute = () => {
  const location = useSafeRouterLocation();
  const history = useHistory();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [
    location.search,
  ]);
  const [cases, setCases] = useState<SavedCase[]>([]);

  useEffect(() => {
    getSavedCases().then((cases) => setCases(cases));
  }, []);

  useEffect(() => {
    saveCases(cases);
  }, [cases]);

  const handleLoadCaseClicked = useCallback(
    (patientCase) => () => {
      evt({
        category: CATEGORY.AGENT,
        action: ACTION.OPEN_CASE,
        label: patientCase.patientId,
      });
      queryParams.set("shareId", patientCase.patientId);
      history.push(`/agent/${AgentRoute.DD}?${queryParams.toString()}`);
    },
    [queryParams, history],
  );

  const handleCaseDelete = useCallback(
    (index) => () =>
      setCases((cases) => [
        ...cases.slice(0, index),
        ...cases.slice(index + 1),
      ]),
    [setCases],
  );

  const byDateDesc = (dateA, dateB) =>
    new Date(dateB.createdOn).getTime() - new Date(dateA.createdOn).getTime();

  const minToMill = (inMinutes) => 1000 * 60 * inMinutes;

  const showMoment = (createdOn) =>
    new Date().getTime() - new Date(createdOn).getTime() <=
    minToMill(MAX_NUMBER_IN_MINUTES_TO_SHOW_MOMENT_TEXT);

  const goBack = useCallback(() => {
    history.goBack();
  }, [history]);

  return (
    <MyCasesWrapper>
      <MyCasesTitle>
        <Back onClick={goBack} />
        Saved & Shared Cases
      </MyCasesTitle>
      {cases.sort(byDateDesc).map((savedCase, idx) => {
        const momentObj = moment(savedCase.createdOn);
        // Disaply tooltip in client locale
        const dateTooltip = momentObj.format("LLLL");
        const displayDate = showMoment(savedCase.createdOn)
          ? momentObj.fromNow()
          : momentObj.calendar();
        return (
          <CaseItem key={savedCase.patientId}>
            <CaseName>{savedCase.title}</CaseName>
            <CaseCreationDate>
              <AgentTooltip title={dateTooltip}>
                <span>{displayDate}</span>
              </AgentTooltip>
            </CaseCreationDate>
            <OpenCaseButton
              variant="outlined"
              onClick={handleLoadCaseClicked(savedCase)}
              startIcon={<Launch />}
            >
              Load
            </OpenCaseButton>
            <Delete onClick={handleCaseDelete(idx)}>
              <DeleteOutlineIcon />
            </Delete>
          </CaseItem>
        );
      })}
    </MyCasesWrapper>
  );
};
